import React from 'react';
import {
	AssuranceLevelList,
	OSDPLEDOption,
	Reader,
	ReaderError,
	ReaderInterface,
	ReaderLabel,
	ReaderOptionsTab,
	SelectOption,
	SelectTimeZone,
	SupportedReaderClass,
} from '../../../../model/DeviceAdminModel';
import { getDefaultAssuranceLevelSelection } from '../helpers';

export type ReaderContextState = {
	id?: number;
	controllerId: number;
	name: string;
	index: number;
	supportedReaderClass: SupportedReaderClass;
	labels: ReaderLabel[];
	anyReaderOptions: ReaderOptionsTab[];
	cardReaderOptions: ReaderOptionsTab[];
	scramblePadKeyPadOptions: ReaderOptionsTab[];
	genericTimeZones: SelectTimeZone[];
	cardMaps: SelectOption[];
	specialHandlingInstructions: SelectOption[];
	readerThreatLevels: SelectOption[];
	isReaderWireless: boolean;
	rS485ReaderTypes: SelectOption[];
	oSDPAddress: number;
	wLHubAddress: number;
	eACAddress: number;
	wL2HubAddress: number;
	allegionLockAddress: number;
	okDegrade: boolean;
	passbackZones: SelectOption[];
	cCOTZAssurance: boolean;
	assuranceLevels: SelectOption[];
	defaultAssuranceLevels: SelectOption[];
	isOnboardWiegandAvailable: boolean;
	isControllerSNIB3: boolean;
	isControllerMATCH: boolean;
	firmware: string;
	enableReaderBezelTamper: boolean;
	enableHexPassThrough: boolean;
	enableMatchBezelTamper: boolean;
	facilityCodeCardNumber: boolean;
	corporate1000: boolean;
	hexDigitUUID: boolean;
	enableKeyPad: boolean;
	enableOSDPHandshake: boolean;
	isDoorWireless: boolean;
	oSDPTitle: string;
	oSDPConfirmation: string;
	firmwareConfirm: string;
	firmwareWarning: string;
	firmwareTitle: string;
	tSFirmwares: SelectOption[];
	canUpdateFirmware: boolean;
	applyToAllReaders: boolean;
	readerInterface: ReaderInterface;
	isFICAMEnabled: boolean;
	antiPassback: number;
	matchAlgorithm: number;
	fascnHandling: number;
	updateFirmwarePermissionText: string;
	isEntryReaderError: boolean;
	readerErrorType: ReaderError;
	readerErrorMessage: string;
	selections: {
		cardMap: number;
		readerThreatLevel: number;
		cCOTZThreatLevel: number;
		assuranceLevel: number;
		defaultAssuranceLevel: number;
		toZone: number;
		fromZone: number;
		specialHandlingInstruction: number;
		cardCodeOnlyTimeZone: number;
		tSFirmware: number;
		rS485ReaderType: number;
	};
	unpermitted: {
		cardCodeOnlyTimeZone: SelectTimeZone;
	};
	changedOSDPLEDColor: boolean;
	useCustomOSDPLEDColor: boolean;
	osdpLEDOptions: OSDPLEDOption[];
	osdpCustomLEDColors: SelectOption[];
	readerTypeChanged: boolean;
};

enum ReaderActionTypes {
	SET_READER_CONTROLLER_ID = 'SET_READER_CONTROLLER_ID',
	SET_READER_NAME = 'SET_READER_NAME',
	SET_READER_INDEX = 'SET_READER_INDEX',
	SET_READER_SUPPORTED_READER_CLASS = 'SET_READER_SUPPORTED_READER_CLASS',
	SET_READER_LABELS = 'SET_READER_LABELS',
	SET_READER_ANY_READER_OPTIONS = 'SET_READER_ANY_READER_OPTIONS',
	SET_READER_CARD_READER_OPTIONS = 'SET_READER_CARD_READER_OPTIONS',
	SET_READER_SCRAMBLE_PAD_KEY_OPTIONS = 'SET_READER_SCRAMBLE_PAD_KEY_OPTIONS',
	SET_READER_GENERIC_TIME_ZONES = 'SET_READER_GENERIC_TIME_ZONES',
	SET_READER_CARD_MAPS = 'SET_READER_CARD_MAPS',
	SET_READER_SPECIAL_HANDLING_INSTRUCTIONS = 'SET_READER_SPECIAL_HANDLING_INSTRUCTIONS',
	SET_READER_THREAT_LEVELS = 'SET_READER_THREAT_LEVELS',
	SET_READER_IS_READER_WIRELESS = 'SET_READER_IS_READER_WIRELESS',
	SET_READER_RS485_READER_TYPES = 'SET_READER_RS485_READER_TYPES',
	SET_READER_OSDP_ADDRESS = 'SET_READER_OSDP_ADDRESS',
	SET_READER_WLHUB_ADDRESS = 'SET_READER_WLHUB_ADDRESS',
	SET_READER_EAC_ADDRESS = 'SET_READER_EAC_ADDRESS',
	SET_READER_WL2HUB_ADDRESS = 'SET_READER_WL2HUB_ADDRESS',
	SET_READER_ALLEGION_LOCK_ADDRESS = 'SET_READER_ALLEGION_LOCK_ADDRESS',
	SET_READER_OK_DEGRADE = 'SET_READER_OK_DEGRADE',
	SET_READER_PASSBACK_ZONES = 'SET_READER_PASSBACK_ZONES',
	SET_READER_CCOTZ_ASSURANCE = 'SET_READER_CCOTZ_ASSURANCE',
	SET_READER_ASSURANCE_LEVELS = 'SET_READER_ASSURANCE_LEVELS',
	SET_READER_DEFAULT_ASSURANCE_LEVELS = 'SET_READER_DEFAULT_ASSURANCE_LEVELS',
	SET_READER_IS_ONBOARD_WIEGAND_AVAILABLE = 'SET_READER_IS_ONBOARD_WIEGAND_AVAILABLE',
	SET_READER_IS_CONTROLLER_SNIB3 = 'SET_READER_IS_CONTROLLER_SNIB3',
	SET_READER_IS_CONTROLLER_MATCH = 'SET_READER_IS_CONTROLLER_MATCH',
	SET_READER_FIRMWARE = 'SET_READER_FIRMWARE',
	SET_READER_ENABLE_READER_BEZEL_TAMPER = 'SET_READER_ENABLE_READER_BEZEL_TAMPER',
	SET_READER_ENABLE_HEX_PASSTHROUGH = 'SET_READER_ENABLE_HEX_PASSTHROUGH',
	SET_READER_ENABLE_MATCH_BEZEL_TAMPER = 'SET_READER_ENABLE_MATCH_BEZEL_TAMPER',
	SET_READER_FACILITY_CODE_CARD_NUMBER = 'SET_READER_FACILITY_CODE_CARD_NUMBER',
	SET_READER_CORPORATE1000 = 'SET_READER_CORPORATE1000',
	SET_READER_HEX_DIGIT_UUID = 'SET_READER_HEX_DIGIT_UUID',
	SET_READER_ENABLE_KEY_PAD = 'SET_READER_ENABLE_KEY_PAD',
	SET_READER_ENABLE_OSDP_HANDSHAKE = 'SET_READER_ENABLE_OSDP_HANDSHAKE',
	SET_READER_IS_DOOR_WIRELESS = 'SET_READER_IS_DOOR_WIRELESS',
	SET_READER_OSDP_TITLE = 'SET_READER_OSDP_TITLE',
	SET_READER_OSDP_CONFIRMATION = 'SET_READER_OSDP_CONFIRMATION',
	SET_READER_FIRMWARE_CONFIRM = 'SET_READER_FIRMWARE_CONFIRM',
	SET_READER_FIRMWARE_WARNING = 'SET_READER_FIRMWARE_WARNING',
	SET_READER_FIRMWARE_TITLE = 'SET_READER_FIRMWARE_TITLE',
	SET_READER_TS_FIRMWARES = 'SET_READER_TS_FIRMWARES',
	SET_READER_CAN_UPDATE_FIRMWARE = 'SET_READER_CAN_UPDATE_FIRMWARE',
	SET_READER_APPLY_TO_ALL_READERS = 'SET_READER_APPLY_TO_ALL_READERS',
	SET_READER_CARD_MAP = 'SET_CARD_MAP',
	SET_READER_THREAT_LEVEL = 'SET_READER_THREAT_LEVEL',
	SET_READER_CCOTZ_THREAT_LEVEL = 'SET_READER_CCOTZ_THREAT_LEVEL',
	SET_READER_ANTIPASSBACK = 'SET_READER_ANTIPASSBACK',
	SET_READER_MATCH_ALGORITHM = 'SET_READER_MATCH_ALGORITHM',
	SET_READER_FASCN_HANDLING = 'SET_READER_FASCN_HANDLING',
	SET_READER_ASSURANCE_LEVEL = 'SET_READER_ASSURANCE_LEVEL',
	SET_READER_DEFAULT_ASSURANCE_LEVEL = 'SET_READER_DEFAULT_ASSURANCE_LEVEL',
	SET_READER_TO_ZONE = 'SET_READER_TO_ZONE',
	SET_READER_FROM_ZONE = 'SET_READER_FROM_ZONE',
	SET_READER_SPECIAL_HANDLING_INSTRUCTION = 'SET_READER_SPECIAL_HANDLING_INSTRUCTION',
	SET_READER_CARD_CODE_ONLY_TIME_ZONE = 'SET_READER_CARD_CODE_ONLY_TIME_ZONE',
	SET_READER_TS_FIRMWARE = 'SET_READER_TS_FIRMWARE',
	SET_READER_RS485_READER_TYPE = 'SET_READER_RS485_READER_TYPE',
	SET_READER_UNPERMITTED_CARD_CODE_ONLY_TIME_ZONE = 'SET_READER_UNPERMITTED_CARD_CODE_ONLY_TIME_ZONE',
	SET_READER_INTERFACE = 'SET_READER_INTERFACE',
	SET_READER_IS_FICAM_ENABLED = 'SET_READER_IS_FICAM_ENABLED',
	SET_READER_UPDATE_FIRMWARE_PERMISSION_TEXT = 'SET_READER_UPDATE_FIRMWARE_PERMISSION_TEXT',
	SET_READER_ERROR_TYPE = 'SET_READER_ERROR_TYPE',
	SET_READER_ERROR_MESSAGE = 'SET_READER_ERROR_MESSAGE',
	INIT_READER = 'INIT_READER',
	SET_READER_ASSURANCE_INFO = 'SET_READER_ASSURANCE_INFO',
	SET_ENTRY_READER_ERROR = 'SET_ENTRY_READER_ERROR',
	SET_OSDP_LED_OPTIONS = 'SET_OSDP_LED_OPTIONS',
	SET_USE_CUSTOM_OSDP_LED_COLOR = 'SET_USE_CUSTOM_OSDP_LED_COLOR',
	SET_CHANGED_OSDP_LED_COLOR = 'SET_CHANGED_OSDP_LED_COLOR',
}

type contextReaderInitReader = {
	type: ReaderActionTypes.INIT_READER;
	payload: Partial<Reader>;
};

type contextReaderSetAssuranceInfo = {
	type: ReaderActionTypes.SET_READER_ASSURANCE_INFO;
	payload: Partial<AssuranceLevelList>;
};

type contextReaderSetName = {
	type: ReaderActionTypes.SET_READER_NAME;
	payload: string;
};

type contextReaderControllerId = {
	type: ReaderActionTypes.SET_READER_CONTROLLER_ID;
	payload: number;
};

type contextReaderSetIndex = {
	type: ReaderActionTypes.SET_READER_INDEX;
	payload: number;
};

type contextReaderSetSupportedReaderClass = {
	type: ReaderActionTypes.SET_READER_SUPPORTED_READER_CLASS;
	payload: SupportedReaderClass;
};

type contextReaderSetLabels = {
	type: ReaderActionTypes.SET_READER_LABELS;
	payload: ReaderLabel[];
};

type contextReaderSetAnyReaderOptions = {
	type: ReaderActionTypes.SET_READER_ANY_READER_OPTIONS;
	payload: ReaderOptionsTab[];
};

type contextReaderSetCardReaderOptions = {
	type: ReaderActionTypes.SET_READER_CARD_READER_OPTIONS;
	payload: ReaderOptionsTab[];
};

type contextReaderSetScramblePadKeyOptions = {
	type: ReaderActionTypes.SET_READER_SCRAMBLE_PAD_KEY_OPTIONS;
	payload: ReaderOptionsTab[];
};

type contextReaderSetGenericTimeZones = {
	type: ReaderActionTypes.SET_READER_GENERIC_TIME_ZONES;
	payload: SelectTimeZone[];
};

type contextReaderSetCardMaps = {
	type: ReaderActionTypes.SET_READER_CARD_MAPS;
	payload: SelectOption[];
};

type contextReaderSetSpecialHandlingInstructions = {
	type: ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTIONS;
	payload: SelectOption[];
};

type contextReaderSetReaderThreatLevels = {
	type: ReaderActionTypes.SET_READER_THREAT_LEVELS;
	payload: SelectOption[];
};

type contextReaderSetIsReaderWireless = {
	type: ReaderActionTypes.SET_READER_IS_READER_WIRELESS;
	payload: boolean;
};

type contextReaderSetRS485ReaderTypes = {
	type: ReaderActionTypes.SET_READER_RS485_READER_TYPES;
	payload: SelectOption[];
};

type contextReaderSetOSDPAddress = {
	type: ReaderActionTypes.SET_READER_OSDP_ADDRESS;
	payload: number;
};

type contextReaderSetWLHubAddress = {
	type: ReaderActionTypes.SET_READER_WLHUB_ADDRESS;
	payload: number;
};

type contextReaderSetEACAddress = {
	type: ReaderActionTypes.SET_READER_EAC_ADDRESS;
	payload: number;
};

type contextReaderSetWL2HubAddress = {
	type: ReaderActionTypes.SET_READER_WL2HUB_ADDRESS;
	payload: number;
};

type contextReaderSetAllegionLockAddress = {
	type: ReaderActionTypes.SET_READER_ALLEGION_LOCK_ADDRESS;
	payload: number;
};

type contextReaderSetOkDegrade = {
	type: ReaderActionTypes.SET_READER_OK_DEGRADE;
	payload: boolean;
};

type contextReaderSetPassbackZones = {
	type: ReaderActionTypes.SET_READER_PASSBACK_ZONES;
	payload: SelectOption[];
};

type contextReaderSetCCOTZAssurance = {
	type: ReaderActionTypes.SET_READER_CCOTZ_ASSURANCE;
	payload: boolean;
};

type contextReaderSetAssuranceLevels = {
	type: ReaderActionTypes.SET_READER_ASSURANCE_LEVELS;
	payload: SelectOption[];
};

type contextReaderSetDefaultAssuranceLevels = {
	type: ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVELS;
	payload: SelectOption[];
};

type contextReaderSetIsOnboardWiegandAvailable = {
	type: ReaderActionTypes.SET_READER_IS_ONBOARD_WIEGAND_AVAILABLE;
	payload: boolean;
};

type contextReaderSetIsControllerSNIB3 = {
	type: ReaderActionTypes.SET_READER_IS_CONTROLLER_SNIB3;
	payload: boolean;
};

type contextReaderSetIsControllerMatch = {
	type: ReaderActionTypes.SET_READER_IS_CONTROLLER_MATCH;
	payload: boolean;
};

type contextReaderSetFirmware = {
	type: ReaderActionTypes.SET_READER_FIRMWARE;
	payload: string;
};

type contextReaderSetEnableReaderBezelTamper = {
	type: ReaderActionTypes.SET_READER_ENABLE_READER_BEZEL_TAMPER;
	payload: boolean;
};

type contextReaderSetEnableHexPassthrough = {
	type: ReaderActionTypes.SET_READER_ENABLE_HEX_PASSTHROUGH;
	payload: boolean;
};

type contextReaderSetEnableMatchBezelTamper = {
	type: ReaderActionTypes.SET_READER_ENABLE_MATCH_BEZEL_TAMPER;
	payload: boolean;
};

type contextReaderSetFacilityCodeCardNumber = {
	type: ReaderActionTypes.SET_READER_FACILITY_CODE_CARD_NUMBER;
	payload: boolean;
};

type contextReaderSetCorporate1000 = {
	type: ReaderActionTypes.SET_READER_CORPORATE1000;
	payload: boolean;
};

type contextReaderSetHexDigitUUID = {
	type: ReaderActionTypes.SET_READER_HEX_DIGIT_UUID;
	payload: boolean;
};

type contextReaderSetEnableKeyPad = {
	type: ReaderActionTypes.SET_READER_ENABLE_KEY_PAD;
	payload: boolean;
};

type contextReaderSetEnableOSDPHandshake = {
	type: ReaderActionTypes.SET_READER_ENABLE_OSDP_HANDSHAKE;
	payload: boolean;
};

type contextReaderSetIsDoorWireless = {
	type: ReaderActionTypes.SET_READER_IS_DOOR_WIRELESS;
	payload: boolean;
};

type contextReaderSetOSDPTitle = {
	type: ReaderActionTypes.SET_READER_OSDP_TITLE;
	payload: string;
};

type contextReaderSetOSDPConfirmation = {
	type: ReaderActionTypes.SET_READER_OSDP_CONFIRMATION;
	payload: string;
};

type contextReaderSetFirmwareConfirm = {
	type: ReaderActionTypes.SET_READER_FIRMWARE_CONFIRM;
	payload: string;
};

type contextReaderSetFirmwareWarning = {
	type: ReaderActionTypes.SET_READER_FIRMWARE_WARNING;
	payload: string;
};

type contextReaderSetFirmwareTitle = {
	type: ReaderActionTypes.SET_READER_FIRMWARE_TITLE;
	payload: string;
};

type contextReaderSetTSFirmwares = {
	type: ReaderActionTypes.SET_READER_TS_FIRMWARES;
	payload: SelectOption[];
};

type contextReaderSetCanUpdateFirmware = {
	type: ReaderActionTypes.SET_READER_CAN_UPDATE_FIRMWARE;
	payload: boolean;
};

type contextReaderSetApplyToAllReaders = {
	type: ReaderActionTypes.SET_READER_APPLY_TO_ALL_READERS;
	payload: boolean;
};

type contextReaderSetReaderInterface = {
	type: ReaderActionTypes.SET_READER_INTERFACE;
	payload: ReaderInterface;
};

type contextReaderSetCardMap = {
	type: ReaderActionTypes.SET_READER_CARD_MAP;
	payload: number;
};

type contextReaderSetReaderThreatLevel = {
	type: ReaderActionTypes.SET_READER_THREAT_LEVEL;
	payload: number;
};

type contextReaderSetCCOTZThreatLevel = {
	type: ReaderActionTypes.SET_READER_CCOTZ_THREAT_LEVEL;
	payload: number;
};

type contextReaderSetAntipassback = {
	type: ReaderActionTypes.SET_READER_ANTIPASSBACK;
	payload: number;
};

type contextReaderSetMatchAlgorithm = {
	type: ReaderActionTypes.SET_READER_MATCH_ALGORITHM;
	payload: number;
};

type contextReaderSetFascnHandling = {
	type: ReaderActionTypes.SET_READER_FASCN_HANDLING;
	payload: number;
};

type contextReaderSetAssuranceLevel = {
	type: ReaderActionTypes.SET_READER_ASSURANCE_LEVEL;
	payload: number;
};

type contextReaderSetDefaultAssuranceLevel = {
	type: ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVEL;
	payload: number;
};

type contextReadeSetToZone = {
	type: ReaderActionTypes.SET_READER_TO_ZONE;
	payload: number;
};

type contextReaderSetFromZone = {
	type: ReaderActionTypes.SET_READER_FROM_ZONE;
	payload: number;
};

type contextReaderSetSpecialHandlingInstruction = {
	type: ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTION;
	payload: number;
};

type contextReaderSetCardCodeOnlyTimeZone = {
	type: ReaderActionTypes.SET_READER_CARD_CODE_ONLY_TIME_ZONE;
	payload: number;
};

type contextReaderSetTSFirmware = {
	type: ReaderActionTypes.SET_READER_TS_FIRMWARE;
	payload: number;
};

type contextReaderSetRS485ReaderType = {
	type: ReaderActionTypes.SET_READER_RS485_READER_TYPE;
	payload: number;
};

type contextReaderSetIsFICAMEnabled = {
	type: ReaderActionTypes.SET_READER_IS_FICAM_ENABLED;
	payload: boolean;
};

type contextReaderSetUnpermittedCardCodeOnlyTimeZone = {
	type: ReaderActionTypes.SET_READER_UNPERMITTED_CARD_CODE_ONLY_TIME_ZONE;
	payload: SelectTimeZone;
};

type contextReaderSetUpdateFirmwarePermissionText = {
	type: ReaderActionTypes.SET_READER_UPDATE_FIRMWARE_PERMISSION_TEXT;
	payload: string;
};

type contextReaderSetReaderErrorType = {
	type: ReaderActionTypes.SET_READER_ERROR_TYPE;
	payload: ReaderError;
};

type contextReaderSetReaderErrorMessage = {
	type: ReaderActionTypes.SET_READER_ERROR_MESSAGE;
	payload: string;
};

type contextReaderSetEntryReaderError = {
	type: ReaderActionTypes.SET_ENTRY_READER_ERROR;
	payload: boolean;
};

type contextReaderSetOSDPLEDOptions = {
	type: ReaderActionTypes.SET_OSDP_LED_OPTIONS;
	payload: OSDPLEDOption[];
};

type contextReaderSetUseCustomOSDPLEDColor = {
	type: ReaderActionTypes.SET_USE_CUSTOM_OSDP_LED_COLOR;
	payload: boolean;
};

type contextReaderSetChangedOSDLEDColor = {
	type: ReaderActionTypes.SET_CHANGED_OSDP_LED_COLOR;
	payload: boolean;
};

type ReaderActionContext =
	| contextReaderSetName
	| contextReaderControllerId
	| contextReaderSetIndex
	| contextReaderSetSupportedReaderClass
	| contextReaderSetLabels
	| contextReaderSetAnyReaderOptions
	| contextReaderSetCardReaderOptions
	| contextReaderSetScramblePadKeyOptions
	| contextReaderSetGenericTimeZones
	| contextReaderSetCardMaps
	| contextReaderSetSpecialHandlingInstructions
	| contextReaderSetReaderThreatLevels
	| contextReaderSetIsReaderWireless
	| contextReaderSetRS485ReaderTypes
	| contextReaderSetOSDPAddress
	| contextReaderSetWLHubAddress
	| contextReaderSetEACAddress
	| contextReaderSetWL2HubAddress
	| contextReaderSetOkDegrade
	| contextReaderSetPassbackZones
	| contextReaderSetCCOTZAssurance
	| contextReaderSetAssuranceLevels
	| contextReaderSetDefaultAssuranceLevels
	| contextReaderSetIsOnboardWiegandAvailable
	| contextReaderSetIsControllerSNIB3
	| contextReaderSetIsControllerMatch
	| contextReaderSetFirmware
	| contextReaderSetEnableReaderBezelTamper
	| contextReaderSetEnableHexPassthrough
	| contextReaderSetEnableMatchBezelTamper
	| contextReaderSetFacilityCodeCardNumber
	| contextReaderSetCorporate1000
	| contextReaderSetHexDigitUUID
	| contextReaderSetEnableKeyPad
	| contextReaderSetEnableOSDPHandshake
	| contextReaderSetIsDoorWireless
	| contextReaderSetOSDPTitle
	| contextReaderSetFirmwareConfirm
	| contextReaderSetFirmwareWarning
	| contextReaderSetFirmwareTitle
	| contextReaderSetTSFirmwares
	| contextReaderSetCanUpdateFirmware
	| contextReaderSetApplyToAllReaders
	| contextReaderSetCardMap
	| contextReaderSetReaderThreatLevel
	| contextReaderSetCCOTZThreatLevel
	| contextReaderSetAntipassback
	| contextReaderSetMatchAlgorithm
	| contextReaderSetFascnHandling
	| contextReaderSetAssuranceLevel
	| contextReaderSetDefaultAssuranceLevel
	| contextReadeSetToZone
	| contextReaderSetFromZone
	| contextReaderSetSpecialHandlingInstruction
	| contextReaderSetCardCodeOnlyTimeZone
	| contextReaderSetTSFirmware
	| contextReaderSetAllegionLockAddress
	| contextReaderSetOSDPConfirmation
	| contextReaderSetRS485ReaderType
	| contextReaderSetReaderInterface
	| contextReaderSetIsFICAMEnabled
	| contextReaderInitReader
	| contextReaderSetUpdateFirmwarePermissionText
	| contextReaderSetReaderErrorType
	| contextReaderSetReaderErrorMessage
	| contextReaderSetAssuranceInfo
	| contextReaderSetUnpermittedCardCodeOnlyTimeZone
	| contextReaderSetOSDPLEDOptions
	| contextReaderSetUseCustomOSDPLEDColor
	| contextReaderSetChangedOSDLEDColor
	| contextReaderSetEntryReaderError;

const readerState: ReaderContextState = {
	controllerId: 0,
	name: '',
	index: 0,
	supportedReaderClass: SupportedReaderClass.Default,
	labels: [],
	anyReaderOptions: [],
	cardReaderOptions: [],
	scramblePadKeyPadOptions: [],
	genericTimeZones: [],
	cardMaps: [],
	specialHandlingInstructions: [],
	readerThreatLevels: [],
	isReaderWireless: false,
	rS485ReaderTypes: [],
	oSDPAddress: 0,
	wLHubAddress: 0,
	eACAddress: 0,
	wL2HubAddress: 0,
	allegionLockAddress: 0,
	okDegrade: false,
	passbackZones: [],
	cCOTZAssurance: false,
	assuranceLevels: [],
	defaultAssuranceLevels: [],
	isOnboardWiegandAvailable: false,
	isControllerSNIB3: false,
	isControllerMATCH: false,
	firmware: '',
	enableReaderBezelTamper: false,
	enableHexPassThrough: false,
	enableMatchBezelTamper: false,
	facilityCodeCardNumber: false,
	corporate1000: false,
	hexDigitUUID: false,
	enableKeyPad: false,
	enableOSDPHandshake: false,
	isDoorWireless: false,
	oSDPTitle: '',
	oSDPConfirmation: '',
	firmwareConfirm: '',
	firmwareWarning: '',
	firmwareTitle: '',
	tSFirmwares: [],
	canUpdateFirmware: true,
	applyToAllReaders: false,
	readerInterface: ReaderInterface.MatchDS47,
	isFICAMEnabled: false,
	antiPassback: 0,
	matchAlgorithm: 0,
	fascnHandling: 0,
	updateFirmwarePermissionText: '',
	isEntryReaderError: false,
	readerErrorMessage: '',
	readerErrorType: undefined,
	selections: {
		cardMap: 0,
		readerThreatLevel: 0,
		cCOTZThreatLevel: 0,
		assuranceLevel: 0,
		defaultAssuranceLevel: 0,
		toZone: 0,
		fromZone: 0,
		specialHandlingInstruction: 0,
		cardCodeOnlyTimeZone: 1,
		tSFirmware: 0,
		rS485ReaderType: 0,
	},
	unpermitted: {
		cardCodeOnlyTimeZone: null,
	},
	osdpLEDOptions: [],
	useCustomOSDPLEDColor: false,
	changedOSDPLEDColor: false,
	osdpCustomLEDColors: [],
	readerTypeChanged: false,
};

const readerContext = (state: Readonly<ReaderContextState>, action: ReaderActionContext): ReaderContextState => {
	switch (action.type) {
		case ReaderActionTypes.INIT_READER:
			return {
				...state,
				id: action.payload.Id,
				controllerId: action.payload.ControllerId,
				name: action.payload.Name,
				index: action.payload.Index,
				supportedReaderClass: action.payload.SupportedReaderClass,
				labels: action.payload.Labels,
				anyReaderOptions: action.payload.AnyReaderOptions,
				cardReaderOptions: action.payload.CardReaderOptions,
				scramblePadKeyPadOptions: action.payload.ScramblePadKeyPadOptions,
				genericTimeZones: action.payload.GenericTimeZones,
				cardMaps: action.payload.CardMaps,
				specialHandlingInstructions: action.payload.SpecialHandlingInstructions,
				readerThreatLevels: action.payload.ReaderThreatLevels,
				isReaderWireless: action.payload.IsReaderWireless,
				rS485ReaderTypes: action.payload.RS485ReaderTypes,
				oSDPAddress: action.payload.OSDPAddress,
				wLHubAddress: action.payload.WLHubAddress,
				eACAddress: action.payload.EACAddress,
				wL2HubAddress: action.payload.WL2HubAddress,
				allegionLockAddress: action.payload.AllegionLockAddress,
				okDegrade: action.payload.OkDegrade,
				passbackZones: action.payload.PassbackZones,
				cCOTZAssurance: action.payload.CCOTZAssurance,
				isOnboardWiegandAvailable: action.payload.IsOnboardWiegandAvailable,
				isControllerSNIB3: action.payload.IsControllerSNIB3,
				isControllerMATCH: action.payload.IsControllerMATCH,
				firmware: action.payload.Firmware,
				isFICAMEnabled: action.payload.IsFICAMEnabled,
				enableReaderBezelTamper: action.payload.EnableReaderBezelTamper,
				enableHexPassThrough: action.payload.EnableHexPassThrough,
				enableMatchBezelTamper: action.payload.EnableMatchBezelTamper,
				facilityCodeCardNumber: action.payload.FacilityCodeCardNumber,
				corporate1000: action.payload.Corporate1000,
				hexDigitUUID: action.payload.HexDigitUUID,
				enableKeyPad: action.payload.EnableKeyPad,
				enableOSDPHandshake: action.payload.EnableOSDPHandshake,
				isDoorWireless: action.payload.IsDoorWireless,
				oSDPTitle: action.payload.OSDPTitle,
				oSDPConfirmation: action.payload.OSDPConfirmation,
				firmwareConfirm: action.payload.FirmwareConfirm,
				firmwareWarning: action.payload.FirmwareWarning,
				firmwareTitle: action.payload.FirmwareTitle,
				tSFirmwares: action.payload.TSFirmwares,
				canUpdateFirmware: action.payload.CanUpdateFirmware,
				applyToAllReaders: action.payload.ApplyToAllReaders,
				readerInterface: action.payload.ReaderInterface,
				antiPassback: action.payload.AntiPassback,
				matchAlgorithm: action.payload.MatchAlgorithm,
				fascnHandling: action.payload.FascnHandling,
				updateFirmwarePermissionText: action.payload.UpdateFirmwarePermissionText,
				osdpCustomLEDColors: action.payload.OSDPCustomLEDColors,
				useCustomOSDPLEDColor: action.payload.UseCustomOSDPLEDColor,
				selections: {
					...state.selections,
					cardCodeOnlyTimeZone: action.payload.CardCodeOnlyTimeZone.GenericId,
					cardMap: action.payload.CardMap,
					readerThreatLevel: action.payload.DisabledReaderAboveLevelId,
					cCOTZThreatLevel: action.payload.DisabledCCOTZAboveLevelId,
					assuranceLevel: action.payload.AssuranceLevel,
					defaultAssuranceLevel: action.payload.DefaultAssuranceLevel,
					toZone: action.payload.ToZone,
					fromZone: action.payload.FromZone,
					specialHandlingInstruction: action.payload.SpecialHandlingInstruction,
					rS485ReaderType: action.payload.SupportedReaderClass === 0 ? action.payload.RS485ReaderType : undefined,
				},
			};

		case ReaderActionTypes.SET_READER_ASSURANCE_INFO:
			const defaultAssuranceLevelSelection: number = getDefaultAssuranceLevelSelection(
				action.payload.DefaultAssuranceLevels,
				state.selections.defaultAssuranceLevel,
				action.payload.DefaultAssurance,
				state.readerTypeChanged
			);

			return {
				...state,
				assuranceLevels: action.payload.AssuranceLevels,
				defaultAssuranceLevels: action.payload.DefaultAssuranceLevels,
				canUpdateFirmware: action.payload.CanUpdateFirmware,
				selections: { ...state.selections, defaultAssuranceLevel: defaultAssuranceLevelSelection },
			};

		case ReaderActionTypes.SET_READER_CONTROLLER_ID:
			return { ...state, controllerId: action.payload };

		case ReaderActionTypes.SET_READER_NAME:
			return { ...state, name: action.payload };

		case ReaderActionTypes.SET_READER_INDEX:
			return { ...state, index: action.payload };

		case ReaderActionTypes.SET_READER_SUPPORTED_READER_CLASS:
			return { ...state, supportedReaderClass: action.payload };

		case ReaderActionTypes.SET_READER_LABELS:
			return { ...state, labels: action.payload };

		case ReaderActionTypes.SET_READER_ANY_READER_OPTIONS:
			return { ...state, anyReaderOptions: action.payload };

		case ReaderActionTypes.SET_READER_CARD_READER_OPTIONS:
			return { ...state, cardReaderOptions: action.payload };

		case ReaderActionTypes.SET_READER_SCRAMBLE_PAD_KEY_OPTIONS:
			return { ...state, scramblePadKeyPadOptions: action.payload };

		case ReaderActionTypes.SET_READER_GENERIC_TIME_ZONES:
			return { ...state, genericTimeZones: action.payload };

		case ReaderActionTypes.SET_READER_CARD_MAPS:
			return { ...state, cardMaps: action.payload };

		case ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTIONS:
			return { ...state, specialHandlingInstructions: action.payload };

		case ReaderActionTypes.SET_READER_THREAT_LEVELS:
			return { ...state, readerThreatLevels: action.payload };

		case ReaderActionTypes.SET_READER_IS_READER_WIRELESS:
			return { ...state, isReaderWireless: action.payload };

		case ReaderActionTypes.SET_READER_RS485_READER_TYPES:
			return { ...state, rS485ReaderTypes: action.payload };

		case ReaderActionTypes.SET_READER_OSDP_ADDRESS:
			return { ...state, oSDPAddress: action.payload };

		case ReaderActionTypes.SET_READER_WLHUB_ADDRESS:
			return { ...state, wLHubAddress: action.payload };

		case ReaderActionTypes.SET_READER_EAC_ADDRESS:
			return { ...state, eACAddress: action.payload };

		case ReaderActionTypes.SET_READER_WL2HUB_ADDRESS:
			return { ...state, wL2HubAddress: action.payload };

		case ReaderActionTypes.SET_READER_ALLEGION_LOCK_ADDRESS:
			return { ...state, allegionLockAddress: action.payload };

		case ReaderActionTypes.SET_READER_OK_DEGRADE:
			return { ...state, okDegrade: action.payload };

		case ReaderActionTypes.SET_READER_PASSBACK_ZONES:
			return { ...state, passbackZones: action.payload };

		case ReaderActionTypes.SET_READER_CCOTZ_ASSURANCE:
			return { ...state, cCOTZAssurance: action.payload };

		case ReaderActionTypes.SET_READER_ASSURANCE_LEVELS:
			return { ...state, assuranceLevels: action.payload };

		case ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVELS:
			return { ...state, defaultAssuranceLevels: action.payload };

		case ReaderActionTypes.SET_READER_IS_ONBOARD_WIEGAND_AVAILABLE:
			return { ...state, isOnboardWiegandAvailable: action.payload };

		case ReaderActionTypes.SET_READER_IS_CONTROLLER_SNIB3:
			return { ...state, isControllerSNIB3: action.payload };

		case ReaderActionTypes.SET_READER_IS_CONTROLLER_MATCH:
			return { ...state, isControllerMATCH: action.payload };

		case ReaderActionTypes.SET_READER_FIRMWARE:
			return { ...state, firmware: action.payload };

		case ReaderActionTypes.SET_READER_IS_FICAM_ENABLED:
			return { ...state, isFICAMEnabled: action.payload };

		case ReaderActionTypes.SET_READER_ENABLE_READER_BEZEL_TAMPER:
			return { ...state, enableReaderBezelTamper: action.payload };

		case ReaderActionTypes.SET_READER_ENABLE_HEX_PASSTHROUGH:
			return { ...state, enableHexPassThrough: action.payload };

		case ReaderActionTypes.SET_READER_ENABLE_MATCH_BEZEL_TAMPER:
			return { ...state, enableMatchBezelTamper: action.payload };

		case ReaderActionTypes.SET_READER_FACILITY_CODE_CARD_NUMBER:
			return { ...state, facilityCodeCardNumber: action.payload };

		case ReaderActionTypes.SET_READER_CORPORATE1000:
			return { ...state, corporate1000: action.payload };

		case ReaderActionTypes.SET_READER_HEX_DIGIT_UUID:
			return { ...state, hexDigitUUID: action.payload };

		case ReaderActionTypes.SET_READER_ENABLE_KEY_PAD:
			return { ...state, enableKeyPad: action.payload };

		case ReaderActionTypes.SET_READER_ENABLE_OSDP_HANDSHAKE:
			return { ...state, enableOSDPHandshake: action.payload };

		case ReaderActionTypes.SET_READER_IS_DOOR_WIRELESS:
			return { ...state, isDoorWireless: action.payload };

		case ReaderActionTypes.SET_READER_OSDP_TITLE:
			return { ...state, oSDPTitle: action.payload };

		case ReaderActionTypes.SET_READER_OSDP_CONFIRMATION:
			return { ...state, oSDPConfirmation: action.payload };

		case ReaderActionTypes.SET_READER_FIRMWARE_CONFIRM:
			return { ...state, firmwareConfirm: action.payload };

		case ReaderActionTypes.SET_READER_FIRMWARE_WARNING:
			return { ...state, firmwareWarning: action.payload };

		case ReaderActionTypes.SET_READER_FIRMWARE_TITLE:
			return { ...state, firmwareTitle: action.payload };

		case ReaderActionTypes.SET_READER_TS_FIRMWARES:
			return { ...state, tSFirmwares: action.payload };

		case ReaderActionTypes.SET_READER_CAN_UPDATE_FIRMWARE:
			return { ...state, canUpdateFirmware: action.payload };

		case ReaderActionTypes.SET_READER_APPLY_TO_ALL_READERS:
			return { ...state, applyToAllReaders: action.payload };

		case ReaderActionTypes.SET_READER_INTERFACE:
			return { ...state, readerInterface: action.payload };

		case ReaderActionTypes.SET_READER_CARD_MAP:
			return { ...state, selections: { ...state.selections, cardMap: action.payload } };

		case ReaderActionTypes.SET_READER_THREAT_LEVEL:
			return { ...state, selections: { ...state.selections, readerThreatLevel: action.payload } };

		case ReaderActionTypes.SET_READER_CCOTZ_THREAT_LEVEL:
			return { ...state, selections: { ...state.selections, cCOTZThreatLevel: action.payload } };

		case ReaderActionTypes.SET_READER_ANTIPASSBACK:
			return { ...state, antiPassback: action.payload };

		case ReaderActionTypes.SET_READER_MATCH_ALGORITHM:
			return { ...state, matchAlgorithm: action.payload };

		case ReaderActionTypes.SET_READER_FASCN_HANDLING:
			return { ...state, fascnHandling: action.payload };

		case ReaderActionTypes.SET_READER_UPDATE_FIRMWARE_PERMISSION_TEXT:
			return { ...state, updateFirmwarePermissionText: action.payload };

		case ReaderActionTypes.SET_READER_ERROR_TYPE:
			return { ...state, readerErrorType: action.payload };

		case ReaderActionTypes.SET_READER_ERROR_MESSAGE:
			return { ...state, readerErrorMessage: action.payload };

		case ReaderActionTypes.SET_READER_ASSURANCE_LEVEL:
			return { ...state, selections: { ...state.selections, assuranceLevel: action.payload } };

		case ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVEL:
			return { ...state, selections: { ...state.selections, defaultAssuranceLevel: action.payload } };

		case ReaderActionTypes.SET_READER_TO_ZONE:
			return { ...state, selections: { ...state.selections, toZone: action.payload } };

		case ReaderActionTypes.SET_READER_FROM_ZONE:
			return { ...state, selections: { ...state.selections, fromZone: action.payload } };

		case ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTION:
			return { ...state, selections: { ...state.selections, specialHandlingInstruction: action.payload } };

		case ReaderActionTypes.SET_READER_CARD_CODE_ONLY_TIME_ZONE:
			return { ...state, selections: { ...state.selections, cardCodeOnlyTimeZone: action.payload } };

		case ReaderActionTypes.SET_READER_TS_FIRMWARE:
			return { ...state, selections: { ...state.selections, tSFirmware: action.payload } };

		case ReaderActionTypes.SET_READER_RS485_READER_TYPE:
			return { ...state, readerTypeChanged: true, selections: { ...state.selections, rS485ReaderType: action.payload } };

		case ReaderActionTypes.SET_READER_UNPERMITTED_CARD_CODE_ONLY_TIME_ZONE:
			return { ...state, unpermitted: { ...state.unpermitted, cardCodeOnlyTimeZone: action.payload } };

		case ReaderActionTypes.SET_ENTRY_READER_ERROR:
			return { ...state, isEntryReaderError: action.payload };

		case ReaderActionTypes.SET_OSDP_LED_OPTIONS:
			return { ...state, osdpLEDOptions: action.payload };

		case ReaderActionTypes.SET_USE_CUSTOM_OSDP_LED_COLOR:
			return { ...state, useCustomOSDPLEDColor: action.payload };

		case ReaderActionTypes.SET_CHANGED_OSDP_LED_COLOR:
			return { ...state, changedOSDPLEDColor: action.payload };

		default:
			return { ...state };
	}
};

const initReaderAction = (payload: Partial<Reader>): contextReaderInitReader => {
	return {
		type: ReaderActionTypes.INIT_READER,
		payload,
	};
};

const setAssuranceInfo = (payload: Partial<AssuranceLevelList>): contextReaderSetAssuranceInfo => {
	return {
		type: ReaderActionTypes.SET_READER_ASSURANCE_INFO,
		payload,
	};
};

const setName = (payload: string): contextReaderSetName => {
	return {
		type: ReaderActionTypes.SET_READER_NAME,
		payload,
	};
};

const setControllerId = (payload: number): contextReaderControllerId => {
	return {
		type: ReaderActionTypes.SET_READER_CONTROLLER_ID,
		payload,
	};
};

const setIndex = (payload: number): contextReaderSetIndex => {
	return {
		type: ReaderActionTypes.SET_READER_INDEX,
		payload,
	};
};

const setSupportedReaderClass = (payload: SupportedReaderClass): contextReaderSetSupportedReaderClass => {
	return {
		type: ReaderActionTypes.SET_READER_SUPPORTED_READER_CLASS,
		payload,
	};
};

const setLabels = (payload: ReaderLabel[]): contextReaderSetLabels => {
	return {
		type: ReaderActionTypes.SET_READER_LABELS,
		payload,
	};
};

const setAnyReaderOptions = (payload: ReaderOptionsTab[]): contextReaderSetAnyReaderOptions => {
	return {
		type: ReaderActionTypes.SET_READER_ANY_READER_OPTIONS,
		payload,
	};
};

const setCardReaderOptions = (payload: ReaderOptionsTab[]): contextReaderSetCardReaderOptions => {
	return {
		type: ReaderActionTypes.SET_READER_CARD_READER_OPTIONS,
		payload,
	};
};

const setScramblePadKeyOptions = (payload: ReaderOptionsTab[]): contextReaderSetScramblePadKeyOptions => {
	return {
		type: ReaderActionTypes.SET_READER_SCRAMBLE_PAD_KEY_OPTIONS,
		payload,
	};
};

const setGenericTimeZones = (payload: SelectTimeZone[]): contextReaderSetGenericTimeZones => {
	return {
		type: ReaderActionTypes.SET_READER_GENERIC_TIME_ZONES,
		payload,
	};
};

const setCardMaps = (payload: SelectOption[]): contextReaderSetCardMaps => {
	return {
		type: ReaderActionTypes.SET_READER_CARD_MAPS,
		payload,
	};
};

const setIsFICAMEnabled = (payload: boolean): contextReaderSetIsFICAMEnabled => {
	return {
		type: ReaderActionTypes.SET_READER_IS_FICAM_ENABLED,
		payload,
	};
};

const setSpecialHandlingInstructions = (payload: SelectOption[]): contextReaderSetSpecialHandlingInstructions => {
	return {
		type: ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTIONS,
		payload,
	};
};

const setThreatLevels = (payload: SelectOption[]): contextReaderSetReaderThreatLevels => {
	return {
		type: ReaderActionTypes.SET_READER_THREAT_LEVELS,
		payload,
	};
};

const setIsReaderWireless = (payload: boolean): contextReaderSetIsReaderWireless => {
	return {
		type: ReaderActionTypes.SET_READER_IS_READER_WIRELESS,
		payload,
	};
};

const setRS485ReaderTypes = (payload: SelectOption[]): contextReaderSetRS485ReaderTypes => {
	return {
		type: ReaderActionTypes.SET_READER_RS485_READER_TYPES,
		payload,
	};
};

const setOSDPAddress = (payload: number): contextReaderSetOSDPAddress => {
	return {
		type: ReaderActionTypes.SET_READER_OSDP_ADDRESS,
		payload,
	};
};

const setWLHubAddress = (payload: number): contextReaderSetWLHubAddress => {
	return {
		type: ReaderActionTypes.SET_READER_WLHUB_ADDRESS,
		payload,
	};
};

const setEACAddress = (payload: number): contextReaderSetEACAddress => {
	return {
		type: ReaderActionTypes.SET_READER_EAC_ADDRESS,
		payload,
	};
};

const setWL2HubAddress = (payload: number): contextReaderSetWL2HubAddress => {
	return {
		type: ReaderActionTypes.SET_READER_WL2HUB_ADDRESS,
		payload,
	};
};

const setAllegionLockAddress = (payload: number): contextReaderSetAllegionLockAddress => {
	return {
		type: ReaderActionTypes.SET_READER_ALLEGION_LOCK_ADDRESS,
		payload,
	};
};

const setOkDegrade = (payload: boolean): contextReaderSetOkDegrade => {
	return {
		type: ReaderActionTypes.SET_READER_OK_DEGRADE,
		payload,
	};
};

const setPassbackZones = (payload: SelectOption[]): contextReaderSetPassbackZones => {
	return {
		type: ReaderActionTypes.SET_READER_PASSBACK_ZONES,
		payload,
	};
};

const setCCOTZAssurance = (payload: boolean): contextReaderSetCCOTZAssurance => {
	return {
		type: ReaderActionTypes.SET_READER_CCOTZ_ASSURANCE,
		payload,
	};
};

const setAssuranceLevels = (payload: SelectOption[]): contextReaderSetAssuranceLevels => {
	return {
		type: ReaderActionTypes.SET_READER_ASSURANCE_LEVELS,
		payload,
	};
};

const setDefaultAssuranceLevels = (payload: SelectOption[]): contextReaderSetDefaultAssuranceLevels => {
	return {
		type: ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVELS,
		payload,
	};
};

const setIsOnboardWiegandAvailable = (payload: boolean): contextReaderSetIsOnboardWiegandAvailable => {
	return {
		type: ReaderActionTypes.SET_READER_IS_ONBOARD_WIEGAND_AVAILABLE,
		payload,
	};
};

const setIsControllerSNIB3 = (payload: boolean): contextReaderSetIsControllerSNIB3 => {
	return {
		type: ReaderActionTypes.SET_READER_IS_CONTROLLER_SNIB3,
		payload,
	};
};

const setIsControllerMatch = (payload: boolean): contextReaderSetIsControllerMatch => {
	return {
		type: ReaderActionTypes.SET_READER_IS_CONTROLLER_MATCH,
		payload,
	};
};

const setFirmware = (payload: string): contextReaderSetFirmware => {
	return {
		type: ReaderActionTypes.SET_READER_FIRMWARE,
		payload,
	};
};

const setEnableReaderBezelTamper = (payload: boolean): contextReaderSetEnableReaderBezelTamper => {
	return {
		type: ReaderActionTypes.SET_READER_ENABLE_READER_BEZEL_TAMPER,
		payload,
	};
};

const setEnableHexPassthrough = (payload: boolean): contextReaderSetEnableHexPassthrough => {
	return {
		type: ReaderActionTypes.SET_READER_ENABLE_HEX_PASSTHROUGH,
		payload,
	};
};

const setEnableMatchBezelTamper = (payload: boolean): contextReaderSetEnableMatchBezelTamper => {
	return {
		type: ReaderActionTypes.SET_READER_ENABLE_MATCH_BEZEL_TAMPER,
		payload,
	};
};

const setFacilityCodeCardNumber = (payload: boolean): contextReaderSetFacilityCodeCardNumber => {
	return {
		type: ReaderActionTypes.SET_READER_FACILITY_CODE_CARD_NUMBER,
		payload,
	};
};

const setCorporate1000 = (payload: boolean): contextReaderSetCorporate1000 => {
	return {
		type: ReaderActionTypes.SET_READER_CORPORATE1000,
		payload,
	};
};

const setHexDigitUUID = (payload: boolean): contextReaderSetHexDigitUUID => {
	return {
		type: ReaderActionTypes.SET_READER_HEX_DIGIT_UUID,
		payload,
	};
};

const setEnableKeyPad = (payload: boolean): contextReaderSetEnableKeyPad => {
	return {
		type: ReaderActionTypes.SET_READER_ENABLE_KEY_PAD,
		payload,
	};
};

const setEnableOSDPHandshake = (payload: boolean): contextReaderSetEnableOSDPHandshake => {
	return {
		type: ReaderActionTypes.SET_READER_ENABLE_OSDP_HANDSHAKE,
		payload,
	};
};

const setIsDoorWireless = (payload: boolean): contextReaderSetIsDoorWireless => {
	return {
		type: ReaderActionTypes.SET_READER_IS_DOOR_WIRELESS,
		payload,
	};
};

const setOSDPTitle = (payload: string): contextReaderSetOSDPTitle => {
	return {
		type: ReaderActionTypes.SET_READER_OSDP_TITLE,
		payload,
	};
};

const setOSDPConfirmation = (payload: string): contextReaderSetOSDPConfirmation => {
	return {
		type: ReaderActionTypes.SET_READER_OSDP_CONFIRMATION,
		payload,
	};
};

const setFirmwareConfirm = (payload: string): contextReaderSetFirmwareConfirm => {
	return {
		type: ReaderActionTypes.SET_READER_FIRMWARE_CONFIRM,
		payload,
	};
};

const setFirmwareWarning = (payload: string): contextReaderSetFirmwareWarning => {
	return {
		type: ReaderActionTypes.SET_READER_FIRMWARE_WARNING,
		payload,
	};
};

const setFirmwareTitle = (payload: string): contextReaderSetFirmwareTitle => {
	return {
		type: ReaderActionTypes.SET_READER_FIRMWARE_TITLE,
		payload,
	};
};

const setTSFirmwares = (payload: SelectOption[]): contextReaderSetTSFirmwares => {
	return {
		type: ReaderActionTypes.SET_READER_TS_FIRMWARES,
		payload,
	};
};

const setCanUpdateFirmware = (payload: boolean): contextReaderSetCanUpdateFirmware => {
	return {
		type: ReaderActionTypes.SET_READER_CAN_UPDATE_FIRMWARE,
		payload,
	};
};

const setApplyToAllReaders = (payload: boolean): contextReaderSetApplyToAllReaders => {
	return {
		type: ReaderActionTypes.SET_READER_APPLY_TO_ALL_READERS,
		payload,
	};
};

const setReaderInterface = (payload: ReaderInterface): contextReaderSetReaderInterface => {
	return {
		type: ReaderActionTypes.SET_READER_INTERFACE,
		payload,
	};
};

const setCardMap = (payload: number): contextReaderSetCardMap => {
	return {
		type: ReaderActionTypes.SET_READER_CARD_MAP,
		payload,
	};
};

const setReaderThreatLevel = (payload: number): contextReaderSetReaderThreatLevel => {
	return {
		type: ReaderActionTypes.SET_READER_THREAT_LEVEL,
		payload,
	};
};

const setCCOTZThreatLevel = (payload: number): contextReaderSetCCOTZThreatLevel => {
	return {
		type: ReaderActionTypes.SET_READER_CCOTZ_THREAT_LEVEL,
		payload,
	};
};

const setAntiPassback = (payload: number): contextReaderSetAntipassback => {
	return {
		type: ReaderActionTypes.SET_READER_ANTIPASSBACK,
		payload,
	};
};

const setMatchAlgorithm = (payload: number): contextReaderSetMatchAlgorithm => {
	return {
		type: ReaderActionTypes.SET_READER_MATCH_ALGORITHM,
		payload,
	};
};

const setFascnHandling = (payload: number): contextReaderSetFascnHandling => {
	return {
		type: ReaderActionTypes.SET_READER_FASCN_HANDLING,
		payload,
	};
};

const setAssuranceLevel = (payload: number): contextReaderSetAssuranceLevel => {
	return {
		type: ReaderActionTypes.SET_READER_ASSURANCE_LEVEL,
		payload,
	};
};

const setDefaultAssuranceLevel = (payload: number): contextReaderSetDefaultAssuranceLevel => {
	return {
		type: ReaderActionTypes.SET_READER_DEFAULT_ASSURANCE_LEVEL,
		payload,
	};
};

const setToZone = (payload: number): contextReadeSetToZone => {
	return {
		type: ReaderActionTypes.SET_READER_TO_ZONE,
		payload,
	};
};

const setFromZone = (payload: number): contextReaderSetFromZone => {
	return {
		type: ReaderActionTypes.SET_READER_FROM_ZONE,
		payload,
	};
};

const setSpecialHandlingInstruction = (payload: number): contextReaderSetSpecialHandlingInstruction => {
	return {
		type: ReaderActionTypes.SET_READER_SPECIAL_HANDLING_INSTRUCTION,
		payload,
	};
};

const setCardCodeOnlyTimeZone = (payload: number): contextReaderSetCardCodeOnlyTimeZone => {
	return {
		type: ReaderActionTypes.SET_READER_CARD_CODE_ONLY_TIME_ZONE,
		payload,
	};
};

const setTSFirmware = (payload: number): contextReaderSetTSFirmware => {
	return {
		type: ReaderActionTypes.SET_READER_TS_FIRMWARE,
		payload,
	};
};

const setRS485ReaderType = (payload: number): contextReaderSetRS485ReaderType => {
	return {
		type: ReaderActionTypes.SET_READER_RS485_READER_TYPE,
		payload,
	};
};

const setUnpermittedCardCodeOnly = (payload: SelectTimeZone): contextReaderSetUnpermittedCardCodeOnlyTimeZone => {
	return {
		type: ReaderActionTypes.SET_READER_UNPERMITTED_CARD_CODE_ONLY_TIME_ZONE,
		payload,
	};
};

const setUpdateFirmwarePermissionText = (payload: string): contextReaderSetUpdateFirmwarePermissionText => {
	return {
		type: ReaderActionTypes.SET_READER_UPDATE_FIRMWARE_PERMISSION_TEXT,
		payload,
	};
};

const setReaderErrorType = (payload: ReaderError): contextReaderSetReaderErrorType => {
	return {
		type: ReaderActionTypes.SET_READER_ERROR_TYPE,
		payload,
	};
};

const setReaderErrorMessage = (payload: string): contextReaderSetReaderErrorMessage => {
	return {
		type: ReaderActionTypes.SET_READER_ERROR_MESSAGE,
		payload,
	};
};

const setEntryReaderError = (payload: boolean): contextReaderSetEntryReaderError => {
	return {
		type: ReaderActionTypes.SET_ENTRY_READER_ERROR,
		payload,
	};
};

const setOSDPLEDOptions = (payload: OSDPLEDOption[]): contextReaderSetOSDPLEDOptions => {
	return {
		type: ReaderActionTypes.SET_OSDP_LED_OPTIONS,
		payload,
	};
};

const setUseCustomOSDPLEDColor = (payload: boolean): contextReaderSetUseCustomOSDPLEDColor => {
	return {
		type: ReaderActionTypes.SET_USE_CUSTOM_OSDP_LED_COLOR,
		payload,
	};
};

const setChangedOSDPLEDColor = (payload: boolean): contextReaderSetChangedOSDLEDColor => {
	return {
		type: ReaderActionTypes.SET_CHANGED_OSDP_LED_COLOR,
		payload,
	};
};

type ReaderContext = {
	readerState: Readonly<ReaderContextState>;
	dispatcher: React.Dispatch<ReaderActionContext>;
};

const StoreContext = React.createContext<ReaderContext>({ readerState, dispatcher: null });

export {
	readerContext,
	readerState,
	setName,
	setControllerId,
	setIndex,
	setSupportedReaderClass,
	setLabels,
	setAnyReaderOptions,
	setCardReaderOptions,
	setScramblePadKeyOptions,
	setGenericTimeZones,
	setCardMaps,
	setSpecialHandlingInstructions,
	setThreatLevels,
	setIsReaderWireless,
	setRS485ReaderTypes,
	setOSDPAddress,
	setWLHubAddress,
	setEACAddress,
	setWL2HubAddress,
	setAllegionLockAddress,
	setOkDegrade,
	setPassbackZones,
	setCCOTZAssurance,
	setAssuranceLevels,
	setDefaultAssuranceLevels,
	setIsOnboardWiegandAvailable,
	setIsControllerSNIB3,
	setIsControllerMatch,
	setFirmware,
	setEnableReaderBezelTamper,
	setEnableHexPassthrough,
	setEnableMatchBezelTamper,
	setFacilityCodeCardNumber,
	setCorporate1000,
	setHexDigitUUID,
	setEnableKeyPad,
	setEnableOSDPHandshake,
	setIsDoorWireless,
	setOSDPTitle,
	setOSDPConfirmation,
	setFirmwareConfirm,
	setFirmwareWarning,
	setFirmwareTitle,
	setTSFirmwares,
	setCanUpdateFirmware,
	setApplyToAllReaders,
	setCardMap,
	setReaderThreatLevel,
	setCCOTZThreatLevel,
	setAntiPassback,
	setFascnHandling,
	setMatchAlgorithm,
	setAssuranceLevel,
	setDefaultAssuranceLevel,
	setToZone,
	setFromZone,
	setSpecialHandlingInstruction,
	setCardCodeOnlyTimeZone,
	setTSFirmware,
	setUnpermittedCardCodeOnly,
	setRS485ReaderType,
	setReaderInterface,
	setIsFICAMEnabled,
	initReaderAction,
	setUpdateFirmwarePermissionText,
	setReaderErrorType,
	setReaderErrorMessage,
	setAssuranceInfo,
	setEntryReaderError,
	setOSDPLEDOptions,
	setUseCustomOSDPLEDColor,
	setChangedOSDPLEDColor,
	StoreContext,
};
